import { type AfterViewInit, Component, DestroyRef, effect, HostListener, inject, Injector, input, runInInjectionContext, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleMap, MapAdvancedMarker, type MapAnchorPoint, MapInfoWindow } from '@angular/google-maps';
import { countryHelpers, DEFAULT_COUNTRY, EntityStatus } from '@core';
import { IncidentSeverity } from '@models';
import { Store } from '@ngrx/store';
import { MapFilterService } from '@services';

import { type LocationData } from '../../models';
import { SpaceCardInfoComponent } from '../space-card-info';
import { MarkerIconPipe } from './marker-icon.pipe';

type DashboardMapMarker = LocationData & {
  options: google.maps.marker.AdvancedMarkerElementOptions;
  position: google.maps.LatLngLiteral;
};

@Component({
  imports: [GoogleMap, MapAdvancedMarker, MapInfoWindow, SpaceCardInfoComponent, MarkerIconPipe],
  selector: 'avs-locations-map',
  templateUrl: './map.component.html',
})
export class LocationsMapComponent implements AfterViewInit {
  locations = input.required<LocationData[]>();

  @ViewChild(MapInfoWindow) protected infoWindow!: MapInfoWindow;
  protected mapFilterService = inject(MapFilterService);
  protected store = inject(Store);
  protected destroyRef = inject(DestroyRef);
  protected selectedSite: LocationData | null = null;

  protected mapOptions: google.maps.MapOptions = {
    disableDoubleClickZoom: true,
    fullscreenControl: false,
    mapId: '2a6238c26271516b',
    mapTypeControl: false,
    scrollwheel: false,
    streetViewControl: false,
  };
  protected markers: DashboardMapMarker[] = [];
  protected filteredMarkers: DashboardMapMarker[] = [];
  protected map: google.maps.Map | undefined = undefined;

  private injector = inject(Injector);

  ngAfterViewInit() {
    runInInjectionContext(this.injector, () => {
      effect(async () => {
        this.locations();

        await this.updateMarkers();
      });
    });
  }

  protected isValidLatitude = (lat: number) => isFinite(lat) && Math.abs(lat) <= 90;
  protected isValidLongitude = (lng: number) => isFinite(lng) && Math.abs(lng) <= 180;

  @HostListener('mouseleave')
  protected disableScrollWheel() {
    this.map?.setOptions({ scrollwheel: false });
  }

  @HostListener('click')
  protected enableScrollWheel() {
    this.map?.setOptions({ scrollwheel: true });
  }

  protected async mapInitialized(map: google.maps.Map) {
    this.map = map;
    await this.createMarkers();
    this.mapFilterService.mapFilter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(selectedFilters => {
      this.filterMarkers(selectedFilters);
    });
  }

  protected async createMarkers() {
    if (this.map) {
      const bounds = new google.maps.LatLngBounds();
      const markers: DashboardMapMarker[] = [];

      this.locations().forEach(location => {
        if (
          !this.isValidLatitude(location.geographic.lat) ||
          !this.isValidLongitude(location.geographic.long) ||
          location.status === EntityStatus.Archived
        ) {
          return;
        }
        const latLng = new google.maps.LatLng(location.geographic.lat, location.geographic.long);
        const marker = {
          ...location,
          options: {},
          position: latLng.toJSON(),
        };

        markers.push(marker);
        bounds.extend(latLng);
      });
      this.markers = markers;
      this.filterMarkers(this.mapFilterService.mapFilter);

      if (this.markers.length > 1) {
        this.map.fitBounds(bounds);
      }

      if (this.markers.length === 1) {
        this.map.setCenter(bounds.getCenter());
        this.map.setZoom(13);
      }

      if (!this.markers.length) {
        await this.initCountryOnMap();
      }
    }
  }

  protected async initCountryOnMap() {
    const geocodeResult = await countryHelpers.getMapBoundariesByCountry(DEFAULT_COUNTRY.name);

    if (geocodeResult.geometry.bounds) {
      this.map?.fitBounds(geocodeResult.geometry.bounds);
    }
  }

  protected openInfo(marker: MapAdvancedMarker, site: LocationData, window: Element) {
    const element: MapAnchorPoint = {
      getAnchor() {
        return marker.advancedMarker;
      },
    };

    if (site.devicesByStatuses && site.incidentCountBySeverity) {
      this.selectedSite = site;
      this.infoWindow.open(element, false, window);
    }
  }

  protected hideInfo() {
    this.infoWindow.close();
  }

  protected filterMarkers(selectedMapFilters: string[]): void {
    this.filteredMarkers = selectedMapFilters.length > 0
      ? this.markers.filter(marker => this.doesMarkerMatchFilters(marker, selectedMapFilters))
      : this.markers;
  }

  private doesMarkerMatchFilters(marker: DashboardMapMarker, selectedMapFilters: string[]): boolean {
    if (!marker.incidentCountBySeverity) {
      return true; // If there's no severity data, include the marker
    }

    const isPaused = selectedMapFilters.includes(EntityStatus.Paused) && marker.status === EntityStatus.Paused;

    const hasCriticalIncidents = selectedMapFilters.includes(IncidentSeverity.CRITICAL) &&
      marker.status === EntityStatus.Active &&
      marker.incidentCountBySeverity.critical > 0;

    const hasMajorIncidents = selectedMapFilters.includes(IncidentSeverity.MAJOR) &&
      marker.status === EntityStatus.Active &&
      marker.incidentCountBySeverity.major > 0;

    const hasMinorIncidents = selectedMapFilters.includes(IncidentSeverity.MINOR) &&
      marker.status === EntityStatus.Active &&
      marker.incidentCountBySeverity.minor > 0;

    const isGood = selectedMapFilters.includes('good') &&
      marker.status === EntityStatus.Active &&
      !marker.incidentCountBySeverity.minor &&
      !marker.incidentCountBySeverity.major &&
      !marker.incidentCountBySeverity.critical;

    return isPaused || hasCriticalIncidents || hasMajorIncidents || hasMinorIncidents || isGood;
  }

  private async updateMarkers() {
    this.markers = [];
    this.filteredMarkers = [];

    await this.createMarkers();
  }
}
